import {
  ActivityWebhook,
  validateEloquaFormIdOrganization,
  retrieveGetCampaignMemberStatuses,
  getMarketoProgramChannelStatusDetails,
  searchMarketoCampaignFolderOrganization,
  listEventPings,
  createEventPings,
  partialUpdateEventPings,
  destroyEventPings,
  EventPing,
  EventPingRequest,
  retrieveFetchAggregatedActivitySyncDetails,
  fetchFormIoFieldsFieldMappings,
  listFieldMappings,
  createFieldMappings,
  retrieveFetchActivitySyncDetailsForIntegration,
  verifySlackChannelIdOrganization,
  fetchFormIoFieldsV2FieldMappings,
  ListFieldMappingsAPIQueryParams,
  validateMarketoProgramIdOrganization,
  validateMarketoProgramCrmSolutionInstance,
  createPopulateSalesforceCampaignData,
  searchMarketoFolderCrmSolutionInstance,
  getMarketoProgramStatusCrmSolutionInstance
} from '@goldcast/api/backend';
import logger from '@goldcast/logger';

import { INTEGRATION_TYPE_MAP } from '@/studio/eventIntegration/constants';
import { SourceField } from '@/studio/orgIntegration/store/types';
import {
  parseErrorFromServer,
  parseFieldMappingRequestBody,
  parseIntegrationApiError
} from '@/studio/orgIntegration/store/utils';
import { showErrorNotification } from '@/utils/utils.js';

export default {
  // Salesforce ******************************************************
  connectSalesforceCampaign(context, campaignID) {
    return new Promise((resolve, reject) => {
      context.commit('setConnectSalesforceCampaignLoading', true);
      const userToken = context.rootState.user.trayUserToken;
      const eventID = context.rootGetters['studioEventList/getCurrentEvent'].id;
      const currentSolutionInstanceId = context.rootState.orgIntegration?.currentSolutionInstance?.id;
      createPopulateSalesforceCampaignData({
        id: eventID,
        body: {
          campaign_id: campaignID,
          solution_instance_id: currentSolutionInstanceId
        },
        headers: { 'user-token': userToken.token }
      })
        .then(({ response }) => {
          const { populated_salesforce_campaign_data } = response as Record<string, any>;
          if (populated_salesforce_campaign_data) {
            context.commit(
              'studioEventList/partialUpdateEvent',
              { eventId: eventID, partialEvent: { salesforce_campaign_id: campaignID } },
              { root: true }
            );
          }
          resolve(response);
        })
        .catch(error => {
          const parsedError = parseIntegrationApiError(error);
          reject(parsedError);
        })
        .finally(() => {
          context.commit('setConnectSalesforceCampaignLoading', false);
        });
    });
  },
  async fetchSalesforceCampaignMemberStatus(context, campaignID) {
    context.commit('setIsSalesforceCampaignStatusLoading', true);
    try {
      const userToken = context.rootState.user.trayUserToken;
      const eventID = context.rootGetters['studioEventList/getCurrentEvent'].id;
      const res = await retrieveGetCampaignMemberStatuses({
        id: eventID,
        queryParams: { campaign_id: campaignID },
        headers: { 'user-token': userToken.token }
      });
      return res;
    } catch (error: any) {
      const errorMessage = JSON.parse(error)?.error?.message;
      logger.error(error);
      return errorMessage;
    } finally {
      context.commit('setIsSalesforceCampaignStatusLoading', false);
    }
  },

  // Slack ******************************************************************
  async getSlackChannels(context, solutionID) {
    context.commit('setSlackChannelsLoading', true);
    context.commit('setSlackChannelError', null);

    try {
      const userToken = context.rootState.user.trayUserToken;
      const orgId = context.rootGetters['org/getCurrentOrgId'];
      const url = `/core/organization/${orgId}/get_slack_channels_v2/?solution_id=${solutionID}`,
        method = 'GET';
      const { response } = await context.dispatch(
        'makeapicall',
        {
          url,
          method,
          headers: { 'user-token': userToken.token }
        },
        { root: true }
      );
      context.commit('setSlackChannels', response.channels);
    } catch (error) {
      const parsedError = parseIntegrationApiError(error);
      context.commit('setSlackChannelError', parsedError);
      showErrorNotification(parsedError.message, 10000);
    } finally {
      context.commit('setSlackChannelsLoading', false);
    }
  },

  async validateSlackChannelId(context, channelId) {
    context.commit('setValidateSlackChannelIDLoading', true);
    try {
      const userToken = context.rootState?.user?.trayUserToken;
      const orgID = context.rootState?.org?.currentOrg.id;
      const solutionID = context.rootState?.orgIntegration?.currentSolutionInstance?.solutionId;
      const res = await verifySlackChannelIdOrganization({
        id: orgID,
        queryParams: { channel_id: channelId, solution_id: solutionID },
        headers: { 'user-token': userToken.token }
      });
      return res;
    } catch (error: any) {
      const errorMessage = JSON.parse(error)?.message;
      return errorMessage;
    } finally {
      context.commit('setValidateSlackChannelIDLoading', false);
    }
  },

  // Pardot ******************************************************************
  async validateAndAddPardotList(context, { listID, activityType = 'SYNC_PARDOT', entityValue = null }) {
    context.commit(activityType === 'SYNC_PARDOT' ? 'setPardotImportLoading' : 'setPardotExportLoading', true);
    context.commit('setPardotError', null);

    interface ReqBody {
      list_id: number;
      activity_type: string;
      entity_value?: string;
    }

    try {
      const userToken = context.rootState.user.trayUserToken;
      const eventID = context.rootGetters['studioEventList/getCurrentEvent'].id;

      const url = `/event/${eventID}/update_pardot_list_id_v2/`;
      const method = 'POST';
      let body: ReqBody = {
        list_id: Number(listID),
        activity_type: activityType
      };
      if (entityValue) {
        body = { ...body, entity_value: entityValue };
      }

      const { response } = await context.dispatch(
        'makeapicall',
        {
          url,
          method,
          headers: { 'user-token': userToken.token },
          body: JSON.stringify(body)
        },
        { root: true }
      );

      if (!response.valid_list_id) {
        return 'Pardot List ID is not found!';
      }

      context.commit('studioEventList/setCurrentEventPardotList', response.pardot_list, { root: true });

      return '';
    } catch (error) {
      const parsedError = parseIntegrationApiError(error);
      context.commit('setPardotError', parsedError);
      return parsedError.message;
    } finally {
      context.commit(activityType === 'SYNC_PARDOT' ? 'setPardotImportLoading' : 'setPardotExportLoading', false);
    }
  },

  // Wistia ******************************************************************
  async fetchWistiaProjects(context) {
    if (context.state.wistiaProjects.length > 0) {
      return;
    }
    context.commit('setIsWistiaLoading', true);
    const eventID = context.rootGetters['studioEventList/getCurrentEvent'].id;

    try {
      const url = `/event/${eventID}/fetch_wistia_existing_projects_v2/`;
      const method = 'GET';
      const userToken = context.rootState.user.trayUserToken;

      const { response, error } = await context.dispatch(
        'makeapicall',
        {
          url,
          method,
          headers: { 'user-token': userToken.token }
        },
        { root: true }
      );
      if (error) {
        throw new Error(JSON.stringify(error));
      }
      context.commit('setWistiaProjects', response.wistia_projects);
    } catch (error) {
      const parsedError = parseIntegrationApiError(error);
      return parsedError.message;
    } finally {
      context.commit('setIsWistiaLoading', false);
    }
  },

  // Marketo *****************************************************************
  async validateMarketoProgramID(context, programID) {
    context.commit('setIsMarketoValidateProgramIdLoading', true);

    try {
      const userToken = context.rootState.user.trayUserToken;
      const currentOrg = context.rootState.org.currentOrg;
      const currentSolutionInstanceId = context.rootState.orgIntegration?.currentSolutionInstance?.id;
      const res = await validateMarketoProgramIdOrganization({
        id: currentOrg.id,
        queryParams: {
          program_id: programID,
          solution_instance_id: currentSolutionInstanceId
        },
        headers: { 'user-token': userToken.token }
      });
      return res;
    } catch (error: any) {
      const errorMessage = error?.message;
      return errorMessage;
    } finally {
      context.commit('setIsMarketoValidateProgramIdLoading', false);
    }
  },

  async validateMarketoProgramIDwithoutTray(context, programID) {
    context.commit('setIsMarketoValidateProgramIdLoading', true);

    try {
      const currentOrg = context.rootState.org.currentOrg;
      const currentSolutionInstanceId = context.rootState.orgIntegration?.currentSolutionInstance?.id;
      const res = await validateMarketoProgramCrmSolutionInstance({
        solution_instance_id: currentSolutionInstanceId,
        queryParams: {
          program_id: programID,
          ...(currentOrg.useInhouseIntegrationAuth && { solution_instance_id: currentSolutionInstanceId })
        },
        headers: {}
      });

      return res.response;
    } catch (error: any) {
      const errorMessage = error?.message;
      return errorMessage;
    } finally {
      context.commit('setIsMarketoValidateProgramIdLoading', false);
    }
  },

  async fetchMarketoCampaignFolders(context, folderName) {
    //handling empty folder name
    if (!folderName?.trim()) {
      return;
    }
    context.commit('setIsMarketoCampaignFoldersLoading', true);
    try {
      const userToken = context.rootState.user.trayUserToken;
      const currentOrg = context.rootState.org.currentOrg;
      const currentSolutionInstance = context.rootState.orgIntegration?.currentSolutionInstance;
      const currentSolutionInstanceId = context.rootState.orgIntegration?.currentSolutionInstance?.id;
      const useWithoutTray = currentOrg?.UseInHouseIntegrationAuth && currentSolutionInstance?.isNativeIntegration;
      const result = useWithoutTray
        ? await searchMarketoFolderCrmSolutionInstance({
            solution_instance_id: currentSolutionInstanceId,
            queryParams: {
              search_name: folderName
            }
          })
        : await searchMarketoCampaignFolderOrganization({
            id: currentOrg?.id,
            queryParams: {
              search_name: folderName,
              solution_instance_id: currentSolutionInstanceId
            },
            headers: { 'user-token': userToken.token }
          });

      context.commit('setMarketoCampaignFolders', result.response.folders);
    } catch (error: any) {
      const errorMessage = JSON.parse(error)?.error?.message;
      context.dispatch(
        'studioToast/triggerToast',
        {
          mode: 'error',
          msg: errorMessage
        },
        { root: true }
      );
    } finally {
      context.commit('setIsMarketoCampaignFoldersLoading', false);
    }
  },
  async cloneMarketoProgram(context, { programName, campaignFolderID }) {
    context.commit('setIsMarketoCloneLoading', true);

    try {
      const userToken = context.rootState.user.trayUserToken;
      const baseProgramID = context.rootGetters['org/marketoConnectedBaseProgramID'];
      const eventID = context.rootGetters['studioEventList/getCurrentEvent'].id;
      const currentOrg = context.rootState?.org?.currentOrg;
      const currentSolutionInstance = context.rootState.orgIntegration?.currentSolutionInstance;
      const currentSolutionInstanceId = currentSolutionInstance?.id;
      const useWithoutTray = currentOrg?.UseInHouseIntegrationAuth && currentSolutionInstance?.isNativeIntegration;
      let res: any;
      if (useWithoutTray) {
        const url = `/core/crm-solution-instance/${currentSolutionInstanceId}/clone-marketo-program/`;
        const method = 'POST';
        const body = JSON.stringify({
          base_program_id: String(baseProgramID),
          name: programName,
          folder_id: Number(campaignFolderID)
        });
        res = await context.dispatch(
          'makeapicall',
          {
            url,
            method,
            body
          },
          { root: true }
        );
      } else {
        const url = `/event/${eventID}/clone_marketo_program/`,
          method = 'POST';
        const body = JSON.stringify({
          base_program_id: baseProgramID,
          program_name: programName,
          campaign_folder_id: campaignFolderID,
          solution_instance_id: currentSolutionInstanceId
        });
        res = await context.dispatch(
          'makeapicall',
          {
            url,
            method,
            body,
            headers: { 'user-token': userToken.token }
          },
          { root: true }
        );
      }
      const errorMessage = res?.error?.message || res?.detail || (res?.error === true && res?.message) || null;
      if (errorMessage) {
        context.dispatch(
          'studioToast/triggerToast',
          {
            mode: 'error',
            msg: errorMessage
          },
          { root: true }
        );
        return { ok: false, error: errorMessage, raw: res };
      }
      const responseData = res?.response ?? res;
      context.dispatch(
        'studioToast/triggerToast',
        {
          mode: 'success',
          msg: 'Marketo program cloned and connected successfully.'
        },
        { root: true }
      );
      return { ok: true, response: responseData, raw: res };
    } catch (error: any) {
      const errorMessage = (() => {
        try {
          const parsed = JSON.parse(error?.message);
          return parsed?.error?.message ?? parsed?.message ?? error?.message;
        } catch {
          return error?.message ?? 'Failed to clone Marketo program.';
        }
      })();
      context.dispatch(
        'studioToast/triggerToast',
        {
          mode: 'error',
          msg: errorMessage
        },
        { root: true }
      );
      return { ok: false, error: errorMessage, raw: error };
    } finally {
      context.commit('setIsMarketoCloneLoading', false);
    }
  },

  async fetchMarketoProgramMemberStatus(context, programID) {
    context.commit('setIsMarketoProgramStatusLoading', true);
    try {
      const userToken = context.rootState.user.trayUserToken;
      const currentOrg = context.rootState.org.currentOrg;
      const currentSolutionInstanceId = context.rootState.orgIntegration?.currentSolutionInstance?.id;
      const currentSolutionInstance = context.rootState.orgIntegration?.currentSolutionInstance;
      const useWithoutTray = currentOrg?.UseInHouseIntegrationAuth && currentSolutionInstance?.isNativeIntegration;
      const res = useWithoutTray
        ? await getMarketoProgramStatusCrmSolutionInstance({
            solution_instance_id: currentSolutionInstanceId,
            queryParams: {
              program_id: programID
            }
          })
        : await getMarketoProgramChannelStatusDetails({
            id: currentOrg?.id,
            queryParams: {
              program_id: programID,
              solution_instance_id: currentSolutionInstanceId
            },
            headers: { 'user-token': userToken.token }
          });
      return res;
    } catch (error: any) {
      const errorMessage = JSON.parse(error)?.error?.message;
      logger.error(error);
      return errorMessage;
    } finally {
      context.commit('setIsMarketoProgramStatusLoading', false);
    }
  },

  // Webhooks ****************************************************************
  fetchWebhooks(context, force = false) {
    if (context.state.webhooks.length > 0 && !force) {
      return;
    }
    context.commit('setWebhooksLoading', true);

    const eventId = context.rootGetters['studioEventList/getCurrentEvent'].id;
    return new Promise((resolve, reject) => {
      context
        .dispatch('makeapicall', { url: `/event/activity-webhooks/?event=${eventId}`, method: 'GET' }, { root: true })
        .then(webhooks => {
          context.commit('setWebhooks', webhooks);
          resolve(webhooks);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          context.commit('setWebhooksLoading', false);
        });
    });
  },
  createWebhook(context, webhookData: Partial<ActivityWebhook>) {
    const eventId = context.rootGetters['studioEventList/getCurrentEvent'].id;
    return new Promise((resolve, reject) => {
      context.commit('setWebhooksLoading', true);
      context
        .dispatch(
          'makeapicall',
          {
            url: `/event/activity-webhooks/`,
            method: 'POST',
            body: JSON.stringify({ ...webhookData, event: eventId })
          },
          { root: true }
        )
        .then(newWebhook => {
          context.commit('setWebhooks', [newWebhook, ...context.state.webhooks]);
          resolve(newWebhook);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setWebhooksLoading', false);
        });
    });
  },
  updateWebhook(context, webhookData: Partial<ActivityWebhook>) {
    return new Promise((resolve, reject) => {
      context.commit('setWebhooksLoading', true);
      context
        .dispatch(
          'makeapicall',
          {
            url: `/event/activity-webhooks/${webhookData.id}/`,
            method: 'PATCH',
            body: JSON.stringify(webhookData)
          },
          { root: true }
        )
        .then(updatedWebhook => {
          const updatedWebhooks = context.state.webhooks.map(webhook =>
            webhook.id === updatedWebhook.id ? updatedWebhook : webhook
          );
          context.commit('setWebhooks', updatedWebhooks);
          resolve(updatedWebhook);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setWebhooksLoading', false);
        });
    });
  },
  deleteWebhook(context, webhookId: number) {
    return new Promise((resolve, reject) => {
      context.commit('setWebhooksLoading', true);
      context
        .dispatch(
          'makeapicall',
          {
            url: `/event/activity-webhooks/${webhookId}/`,
            method: 'DELETE'
          },
          { root: true }
        )
        .then(() => {
          const updatedWebhooks = context.state.webhooks.filter(webhook => webhook.id !== webhookId);
          context.commit('setWebhooks', updatedWebhooks);
          resolve(updatedWebhooks);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setWebhooksLoading', false);
        });
    });
  },

  // Eloqua ****************************************************************
  async validateEloquaEventFormID(context, formId) {
    context.commit('setIsEloquaValidateEventFormIdLoading', true);
    //connectedEloquaFormId is used here for health check purposes
    const connectedEloquaFormId = context.rootGetters['org/eloquaConnectedEventFormID'];
    try {
      const userToken = context.rootState.user.trayUserToken;
      const orgID = context.rootState.org.currentOrg.id;
      const res = await validateEloquaFormIdOrganization({
        id: orgID,
        queryParams: { eloqua_form_id: formId },
        headers: { 'user-token': userToken.token }
      });
      connectedEloquaFormId &&
        context.commit(
          'setIsValidEloquaFormConnected',
          Boolean((res?.response as Record<string, any>)?.is_valid_eloqua_form_id)
        );
      return res;
    } catch (error: any) {
      connectedEloquaFormId && context.commit('setIsValidEloquaFormConnected', false);
      const errorMessage = JSON.parse(error)?.message;
      logger.error(error);
      return errorMessage;
    } finally {
      context.commit('setIsEloquaValidateEventFormIdLoading', false);
    }
  },

  // SlackPlusSalesforce ****************************************************************
  fetchNotifications(context) {
    context.commit('setIsSlackPlusSalesforceNotificationsLoading', true);
    const eventId = context.rootGetters['studioEventList/getCurrentEvent'].id;
    return new Promise((resolve, reject) => {
      listEventPings({ queryParams: { event: eventId } })
        .then(notifications => {
          context.commit('setSlackPlusSalesforceNotifications', notifications);
          resolve(notifications);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          context.commit('setIsSlackPlusSalesforceNotificationsLoading', false);
        });
    });
  },
  createNotification(context, notificationData: EventPingRequest) {
    return new Promise((resolve, reject) => {
      context.commit('setIsSlackPlusSalesforceNotificationsLoading', true);
      createEventPings({
        body: notificationData
      })
        .then(newNotification => {
          context.commit('setSlackPlusSalesforceNotifications', [
            newNotification,
            ...context.state.slackPlusSalesforceNotifications
          ]);
          resolve(newNotification);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setIsSlackPlusSalesforceNotificationsLoading', false);
        });
    });
  },
  updateNotification(context, notificationData: Partial<EventPing>) {
    return new Promise((resolve, reject) => {
      context.commit('setIsSlackPlusSalesforceNotificationsLoading', true);
      partialUpdateEventPings({
        id: notificationData.id as string,
        body: notificationData
      })
        .then(updatedNotification => {
          const updatedNotifications = context.state.slackPlusSalesforceNotifications.map(notification =>
            notification.id === updatedNotification.id ? updatedNotification : notification
          );
          context.commit('setSlackPlusSalesforceNotifications', updatedNotifications);
          resolve(updatedNotification);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setIsSlackPlusSalesforceNotificationsLoading', false);
        });
    });
  },
  deleteNotification(context, notificationId: string) {
    return new Promise((resolve, reject) => {
      context.commit('setIsSlackPlusSalesforceNotificationsLoading', true);
      destroyEventPings({ id: notificationId })
        .then(() => {
          const updatedNotification = context.state.slackPlusSalesforceNotifications.filter(
            notification => notification.id !== notificationId
          );
          context.commit('setSlackPlusSalesforceNotifications', updatedNotification);
          resolve(updatedNotification);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setIsSlackPlusSalesforceNotificationsLoading', false);
        });
    });
  },

  // Integration Erros **************************************
  async fetchIntegrationsActivitySyncData(context, eventId: string) {
    context.commit('setIsIntegrationsActivitySyncDetailsLoading', true);
    context.commit('setIntegrationsActivitySyncErrorState', false);
    try {
      const res = await retrieveFetchAggregatedActivitySyncDetails({ id: eventId });
      //@ts-ignore
      const integrationsSyncDetails = res?.response?.integrationStatusMap;
      if (integrationsSyncDetails) {
        context.commit('setIntegrationsActivitySyncDetails', integrationsSyncDetails);
      }
    } catch (error: any) {
      context.commit('setIntegrationsActivitySyncErrorState', true);
      context.dispatch(
        'studioToast/triggerToast',
        {
          mode: 'error',
          msg: 'Failed to get activity sync details.Please try again'
        },
        { root: true }
      );
      logger.error(error);
    } finally {
      context.commit('setIsIntegrationsActivitySyncDetailsLoading', false);
    }
  },

  async updateIntegrationActivitySyncDetails(context) {
    context.commit('setIsIntegrationsActivitySyncDetailsLoading', true);
    context.commit('setIntegrationsActivitySyncErrorState', false);
    try {
      const eventID = context.rootGetters['studioEventList/getCurrentEvent']?.id;
      const integrationType = context.rootState?.orgIntegration?.currentSolutionInstance?.type;
      const integrationName = INTEGRATION_TYPE_MAP[integrationType];
      const prevIntegrationsSyncDetails = context.state.integrationsActivitySyncDetails;
      const res = await retrieveFetchActivitySyncDetailsForIntegration({
        id: eventID,
        queryParams: {
          integration_name: integrationName
        }
      });
      const integrationsSyncDetails = res?.response;
      if (integrationsSyncDetails) {
        context.commit('setIntegrationsActivitySyncDetails', {
          ...prevIntegrationsSyncDetails,
          [integrationName]: integrationsSyncDetails
        });
      }
    } catch (error: any) {
      context.commit('setIntegrationsActivitySyncErrorState', true);
      context.dispatch(
        'studioToast/triggerToast',
        {
          mode: 'error',
          msg: 'Failed to get activity sync details.Please try again'
        },
        { root: true }
      );
      logger.error(error);
    } finally {
      context.commit('setIsIntegrationsActivitySyncDetailsLoading', false);
    }
  },

  // Event Field Mapping
  async fetchEventFieldMappingSourceFields(context) {
    context.commit('setEventSourceFieldLoading', true);
    try {
      const eventID = context.rootGetters['studioEventList/getCurrentEvent'].id;
      const regFormInfo = context.rootGetters['studioEventList/getRegistrationFormInfo'];
      let sourceFields: SourceField[] = [];
      if (regFormInfo?.is_global_form) {
        //@ts-ignore
        sourceFields = await fetchFormIoFieldsV2FieldMappings({
          queryParams: {
            form_id: regFormInfo?.id
          }
        });
      } else {
        //@ts-ignore
        sourceFields = await fetchFormIoFieldsFieldMappings({
          queryParams: { event_id: eventID }
        });
      }
      context.commit('setEventSourceFields', sourceFields);
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to fetch Goldcast's event source fields!`,
          mode: 'error'
        },
        { root: true }
      );
      return { error };
    } finally {
      context.commit('setEventSourceFieldLoading', false);
    }
  },
  async fetchEventFieldMappings(context, { crmObjectType, crmType }) {
    if (context.state.eventFieldMappings[crmObjectType].mappings.length !== 0) {
      return;
    }

    context.commit('setEventFieldMappingLoading', {
      crmObjectType,
      isFieldMappingLoading: true
    });

    try {
      const regFormInfo = context.rootGetters['studioEventList/getRegistrationFormInfo'];
      const eventID = context.rootGetters['studioEventList/getCurrentEvent'].id;
      const queryParamsPayload: ListFieldMappingsAPIQueryParams = {
        crm_type: crmType,
        crm_object_type: crmObjectType,
        is_custom_field: false,
        is_active: true,
        ...(regFormInfo?.is_global_form
          ? {
              entity_id: regFormInfo?.id,
              entity_type: 'GLOBAL_FORM'
            }
          : {
              event: eventID
            })
      };
      //@ts-ignore
      let eventFieldMappings: CrmField[] = await listFieldMappings({
        queryParams: queryParamsPayload as ListFieldMappingsAPIQueryParams
      });
      eventFieldMappings = eventFieldMappings.sort((a, b) =>
        a.is_default === b.is_default ? 0 : a.is_default ? -1 : 1
      );
      context.commit('setEventFieldMappings', { crmObjectType, eventFieldMappings });
      context.commit('setPrevEventFieldMappings', {
        crmObjectType,
        prevEventFieldMappings: eventFieldMappings
      });
      const defaultErrorArray = new Array(eventFieldMappings.length).fill(null);
      context.commit('setEventFieldMappingRowError', {
        crmObjectType,
        errorsArray: defaultErrorArray
      });
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to fetch ${crmType}'s event field mappings for ${crmObjectType}!`,
          mode: 'error'
        },
        { root: true }
      );
      return { error };
    } finally {
      context.commit('setEventFieldMappingLoading', {
        crmObjectType,
        isFieldMappingLoading: false
      });
    }
  },
  async saveBulkEventFieldMappings(context, crmObjectType) {
    context.commit('setSaveEventFieldMappingLoading', {
      crmObjectType,
      isSaveEventFieldMappingLoading: true
    });

    const eventFieldMappings = context.state.eventFieldMappings[crmObjectType]?.mappings;
    const eventID = context.rootGetters['studioEventList/getCurrentEvent']?.id;
    const userToken = context.rootState?.user?.trayUserToken;
    const crmType = context.rootState?.orgIntegration?.currentCrmType;
    const currentSolutionInstanceId = context.rootState?.orgIntegration?.currentSolutionInstance?.id;
    try {
      await createFieldMappings({
        body: {
          event: eventID,
          crm_type: crmType,
          crm_object_type: crmObjectType,
          field_mappings: parseFieldMappingRequestBody(eventFieldMappings),
          solution_instance_id: currentSolutionInstanceId
        },
        headers: { 'user-token': userToken.token }
      });
      context.commit('setPrevEventFieldMappings', {
        crmObjectType,
        prevEventFieldMappings: eventFieldMappings
      });
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Event Field mapping created/updated successfully.', mode: 'success' },
        { root: true }
      );
    } catch (error) {
      const parsedError = parseErrorFromServer(error);

      if (parsedError && typeof parsedError === 'string') {
        context.dispatch('studioToast/triggerToast', { msg: parsedError, mode: 'error' }, { root: true });
      } else {
        context.dispatch(
          'studioToast/triggerToast',
          {
            msg: 'Failed to create/update event field mappings! Try again.',
            mode: 'error'
          },
          { root: true }
        );
      }

      if (Array.isArray(parsedError)) {
        context.commit('setEventFieldMappingRowError', {
          crmObjectType,
          errorsArray: parsedError
        });
      }
    } finally {
      context.commit('setSaveEventFieldMappingLoading', {
        crmObjectType,
        isSaveEventFieldMappingLoading: false
      });
    }
  }
};
